import React, { useContext } from "react"
import { graphql, useStaticQuery } from "gatsby"
import usePlatformBlocks from "../shared/hooks/usePlatformBlocks"
import Grid from "../shared/grid"
import { UIContext } from "../shared/layout"
import FadedTitle from "../shared/faded-title"
import { T } from "../../design-system/styles/typography"
import {
  Title,
  visitorIntentImageStyle,
  PlatformBlocks,
  FadedTitleWrapper,
  missedSearchImageStyle,
  dataImageSrc,
  StyledFluid,
  DetailsWrapper,
  DetailsBody,
  visitorIntentStyle,
  missedSearchesStyle,
  dataStyle,
  DemoLink,
  Orange,
} from "./understand-visitors.style"
import Button from "../../design-system/atoms/button"

const UnderstandVisitors = () => {
  const platformBlocksImage = usePlatformBlocks()
  const { state: UIState } = useContext(UIContext)
  const { visitorIntentSrc, missedSearchesSrc, dataSrc, analyticsFade } =
    useStaticQuery(graphql`
      query {
        visitorIntentSrc: file(
          relativePath: {
            eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Analytics Dashboard/visitor_intent.jpg"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 1440, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        missedSearchesSrc: file(
          relativePath: {
            eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Analytics Dashboard/missed_search.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 600, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        dataSrc: file(
          relativePath: {
            eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Analytics Dashboard/data_your_way.jpg"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 600, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        analyticsFade: file(
          relativePath: {
            eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Analytics Dashboard/analytics_faded_title.svg"
          }
        ) {
          publicURL
        }
      }
    `)

  return (
    <Grid>
      <PlatformBlocks fluid={platformBlocksImage.childImageSharp.fluid} />
      <FadedTitleWrapper>
        <FadedTitle
          sm={T.sm.h2`400`}
          md={T.md.h2`400`}
          lg={T.lg.h2`400`}
          title={
            <>
              <Orange>Understand</Orange> Your Visitors
            </>
          }
          image={analyticsFade.publicURL}
          alt="Analytics"
          fadeSettings={{ lg: "left" }}
          textAlign={UIState.breakpoint === "sm" ? "center" : "left"}
        />
      </FadedTitleWrapper>
      <StyledFluid
        styleModifier={visitorIntentImageStyle}
        imgStyle={{ objectFit: "contain" }}
        fluid={visitorIntentSrc.childImageSharp.fluid}
        alt="An image of a laptop computer displaying the analytics dashboard."
      />
      <DetailsWrapper styleModifier={visitorIntentStyle}>
        <Title>Visitor Intent</Title>
        <DetailsBody>
          Discover what your visitors are looking for. Leverage search data to
          serve up highly relevant content, promotions, and events that drive
          increased foot traffic and revenue at your property.
        </DetailsBody>
      </DetailsWrapper>
      <DetailsWrapper styleModifier={missedSearchesStyle}>
        <Title>Missed Searches</Title>
        <DetailsBody>
          Missed Searches allow you to see what locations, categories, or brands
          your visitors are looking for that don’t currently exist at your
          venue. This report can help identify potential areas for investment.
        </DetailsBody>
      </DetailsWrapper>
      <StyledFluid
        styleModifier={missedSearchImageStyle}
        imgStyle={{ objectFit: "contain" }}
        fluid={missedSearchesSrc.childImageSharp.fluid}
        alt="An image showing a customer location search that returns no results."
      />
      <StyledFluid
        styleModifier={dataImageSrc}
        imgStyle={{ objectFit: "contain" }}
        fluid={dataSrc.childImageSharp.fluid}
        alt="An image of a laptop computer with analytics dashboard, and then examples of graphs that can be pulled from the data."
      />
      <DetailsWrapper styleModifier={dataStyle}>
        <Title>Data, Your Way</Title>
        <DetailsBody>
          Both the visual reports and raw data can be exported and pulled into
          any software platform with ease. Explore your venue metrics and track
          trends over time.
        </DetailsBody>
      </DetailsWrapper>
      <Button.ScrollLink
        to="contact-form"
        smooth
        duration={1000}
        variants={{
          sm: {
            gridColumn: "2 / -2",
            justifySelf: "center",
            marginTop: "32px",
            marginBottom: "64px",
            gridRow: "9",
          },
          md: {
            gridColumn: "4 / -4",
            gridRow: "8",
            maxWidth: "282px",
            maxHeight: "46px",
          },
          lg: {
            gridColumn: "2 / 5",
            justifySelf: "left",
            marginTop: "48px",
            marginBottom: "96px",
            gridRow: "5",
            width: "213px"
          },
        }}
      >
        Book A Demo
      </Button.ScrollLink>
    </Grid>
  )
}

export default UnderstandVisitors
